import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { Box } from "@mui/material";
import "mapbox-gl/dist/mapbox-gl.css";

import mapConfig, { MAPBOX_ACCESS_TOKEN } from "../../create/components/scenarioMap/mapConfig";
import { flattenCoords } from "../../create/components/scenarioMap/helperFunctions";

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const LOCATION_SOURCE_ID = "scenario-view-location-source";
const LINE_LAYER_ID = "scenario-view-location-line";
const POINT_LAYER_ID = "scenario-view-location-point";

const getBoundsFromGeometry = (geometry) => {
  const coords = flattenCoords(geometry);
  if (!coords.length) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  coords.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  });

  return [
    [minX, minY],
    [maxX, maxY],
  ];
};

function ScenarioLocationMap({ location }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapConfig.map.style,
      center: mapConfig.map.center,
      zoom: mapConfig.map.zoom,
      minZoom: mapConfig.map.minZoom,
      maxZoom: mapConfig.map.maxZoom,
      pitch: mapConfig.map.pitch,
      bearing: mapConfig.map.bearing,
    });

    mapRef.current = map;

    map.on("load", () => {
      if (!map.getSource(LOCATION_SOURCE_ID)) {
        map.addSource(LOCATION_SOURCE_ID, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
      }

      map.addLayer({
        id: LINE_LAYER_ID,
        type: "line",
        source: LOCATION_SOURCE_ID,
        filter: ["==", ["geometry-type"], "LineString"],
        paint: {
          "line-color": "#4dd0e1",
          "line-width": 5,
          "line-opacity": 0.95,
        },
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
      });

      map.addLayer({
        id: POINT_LAYER_ID,
        type: "circle",
        source: LOCATION_SOURCE_ID,
        filter: ["==", ["geometry-type"], "Point"],
        paint: {
          "circle-radius": 8,
          "circle-color": "#ffb74d",
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
        },
      });

      setMapLoaded(true);
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
      mapRef.current = null;
      setMapLoaded(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !map.getSource(LOCATION_SOURCE_ID)) return;

    const source = map.getSource(LOCATION_SOURCE_ID);
    const features = location
      ? [{ type: "Feature", properties: {}, geometry: location }]
      : [];

    source.setData({
      type: "FeatureCollection",
      features,
    });

    if (!location) return;

    if (location.type === "Point") {
      map.flyTo({
        center: location.coordinates,
        zoom: 15,
        essential: true,
      });
      return;
    }

    const bounds = getBoundsFromGeometry(location);
    if (bounds) {
      map.fitBounds(bounds, {
        padding: 50,
        duration: 0,
      });
    }
  }, [location, mapLoaded]);

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
      <div ref={mapContainerRef} style={mapConfig.container} />
    </Box>
  );
}

export default ScenarioLocationMap;
